import { cn } from '@newfront-insurance/core-ui/v2';
import type { ReactNode } from 'react';

import { AnalyticsUnavailableValue } from './analytics-unavailable-value';
import type { NovaAnalyticsKpi, NovaAnalyticsKpis } from '../../../../queries/nova-analytics';

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

function KpiCard({
  label,
  testId,
  metric,
  percentage = false,
  showDelta = false,
  children,
}: {
  label: string;
  testId: string;
  metric: NovaAnalyticsKpi;
  percentage?: boolean;
  showDelta?: boolean;
  children?: ReactNode;
}) {
  const delta = showDelta && metric.value != null ? metric.delta : null;

  return (
    <div
      data-testid={testId}
      className={cn('border-steel-200/50 flex min-h-[93px] flex-col gap-[5px] rounded-sm border bg-white p-3', {
        'border-b-grass-400': delta?.tone === 'POSITIVE',
        'border-b-fire-400': delta?.tone === 'NEGATIVE',
      })}
    >
      <dt className="text-steel-400 text-sm leading-4">
        {label}
        {children}
      </dt>
      <dd className="text-steel-500 order-first text-[17px] leading-6 font-semibold">
        {metric.value == null ? (
          <AnalyticsUnavailableValue />
        ) : (
          `${numberFormatter.format(metric.value)}${percentage ? '%' : ''}`
        )}
      </dd>
      {delta ? (
        <dd
          className={cn('mt-[3px] flex items-baseline gap-1 text-sm leading-4', {
            'text-grass-400': delta.tone === 'POSITIVE',
            'text-fire-400': delta.tone === 'NEGATIVE',
            'text-steel-400': delta.tone === 'NEUTRAL',
          })}
        >
          {delta.tone !== 'NEUTRAL' ? <span aria-hidden="true">{delta.tone === 'POSITIVE' ? '▲' : '▼'}</span> : null}
          {delta.label}
        </dd>
      ) : null}
    </div>
  );
}

export function NovaAnalyticsKpiCards({ kpis }: { kpis: NovaAnalyticsKpis }) {
  return (
    <dl className="grid grid-cols-1 gap-3 @min-[600px]:grid-cols-2" aria-label="Nova usage metrics">
      <KpiCard
        testId="nova-analytics-kpi-working-day-usage"
        label="Of working days, the average person used Nova"
        metric={kpis.workingDayUsageRate}
        percentage
        showDelta
      />
      <KpiCard
        testId="nova-analytics-kpi-active-people"
        label="People used Nova this week"
        metric={kpis.activePeopleThisWeek}
        showDelta
      />
      <KpiCard
        testId="nova-analytics-kpi-skills-built"
        label="Skills built this quarter"
        metric={kpis.skillsBuiltThisQuarter}
      >
        {kpis.skillsBuiltThisQuarter.secondaryValue != null ? (
          <span className="text-steel-400">
            {' '}
            {numberFormatter.format(kpis.skillsBuiltThisQuarter.secondaryValue)} of them shared
          </span>
        ) : null}
      </KpiCard>
      <KpiCard
        testId="nova-analytics-kpi-dormant-skills"
        label="Shared skills not run the last 30 days"
        metric={kpis.dormantSharedSkills}
      />
    </dl>
  );
}
